/// <reference types="vite/client" />
import { createInertiaApp, router } from "@inertiajs/vue3";
import { QueryClient, VueQueryPlugin } from "@tanstack/vue-query";
import { createApp, h, type DefineComponent } from "vue";

import { registerPwaUpdates } from "@ja/portal-employee/registerPwaUpdates";
import { extractAuthIdentity, setActiveIdentity } from "@ja/portal-query";
import { installRealtimeRequestHeader } from "@ja/realtime";

import "../css/portal.css";

import i18n from "./i18n";
import { AuthenticatedLayout } from "./layouts";
import { getPage, getRegisteredPages, hasPage, type PageModule } from "./utils/pageRegistry";

// Importing this vite-portal-plugin virtual module registers each package's portal
// entries (pages + sidebar components) as a side effect, before resolve() runs.
import "virtual:portal-entries";

// Re-export sidebar component utilities for packages to use
export {
  getSidebarComponent,
  hasSidebarComponent,
  registerSidebarComponent
} from "./utils/sidebarComponents";

// Re-export page registry utilities for packages to use
export {
  getPage,
  getRegisteredPages,
  hasPage,
  registerPage
} from "./utils/pageRegistry";

/** Resolved Inertia page component, optionally carrying a persistent layout. */
type PageComponent = DefineComponent & { layout?: unknown };

// Install X-Realtime-Connection-Id header interceptor for self-origin dedup.
installRealtimeRequestHeader();

// Register PWA service worker (shared across employee + admin portals)
registerPwaUpdates();

// QueryClient with offline-friendly defaults (shared across all portals)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24h — keep cache longer for offline
      staleTime: 1000 * 60 * 5,     // 5min
      retry: 1,
    },
  },
});

// Track pending visit URL for cross-portal navigation fallback.
let pendingVisitUrl = '';
router.on('before', (event) => {
  pendingVisitUrl = `${event.detail.visit.url}`;
});

/**
 * PortalAuth Entry Point
 *
 * Standalone Inertia app for the authentication flow.
 * - Login/auth pages (components/) render without layout
 * - Authenticated pages (pages/) use AuthenticatedLayout for unified sidebar
 */
void createInertiaApp({
  title: (title) => title ? `${title} - ${document.title}` : document.title,
  resolve: async (name: string): Promise<PageComponent> => {
    // Special case: Package::Page syntax
    let packageName: string | null = null;
    let pageName = name;

    if (name.includes("::")) {
      [packageName, pageName] = name.split("::");
    }

    // 1. Check local components (auth pages - no layout)
    const localComponents = import.meta.glob<PageModule>(
      "./components/**/*.vue"
    );
    const localComponentPath = `./components/${pageName}.vue`;

    if (!packageName && localComponents[localComponentPath]) {
      const module = await (
        localComponents[localComponentPath] as () => Promise<PageModule>
      )();
      // Return the component directly (auth pages have no layout)
      return module.default as PageComponent;
    }

    // 2. Check local pages (security pages - with layout)
    const localPages = import.meta.glob<PageModule>("./pages/**/*.vue");
    const localPagePath = `./pages/${pageName}.vue`;
    if (
      (!packageName || packageName === "portal-auth") &&
      localPages[localPagePath]
    ) {
      const module = await (
        localPages[localPagePath] as () => Promise<PageModule>
      )();
      const page = module.default as PageComponent;
      page.layout = page.layout ?? AuthenticatedLayout;
      return page;
    }

    // 3. Check pages in the page registry (registered by external packages)
    // Packages register their pages via portalEntry() in PackageConfig
    if (packageName) {
      // Try exact match with package name (e.g., "portal-crudmanager::pages/Dashboard")
      const registryKey = `${packageName}::${pageName}`;
      if (hasPage(registryKey)) {
        const pageOrFunction = getPage(registryKey)!;
        const module =
          typeof pageOrFunction === "function"
            ? await (pageOrFunction as () => Promise<PageModule>)()
            : (pageOrFunction as PageModule);

        const page = module.default as PageComponent;
        page.layout = page.layout ?? AuthenticatedLayout;
        return page;
      }
    } else {
      // No package specified - search all registered pages for a match
      // This provides backward compatibility for routes that don't specify package
      const registeredPages = getRegisteredPages();
      for (const key of registeredPages) {
        // Key format: "package-name::pages/PageName"
        if (key.endsWith(`::${pageName}`)) {
          const pageOrFunction = getPage(key)!;
          const module =
            typeof pageOrFunction === "function"
              ? await (pageOrFunction as () => Promise<PageModule>)()
              : (pageOrFunction as PageModule);

          const page = module.default as PageComponent;
          page.layout = page.layout ?? AuthenticatedLayout;
          return page;
        }
      }
    }

    // Page not in this portal — navigate with full page reload
    // so the server serves the correct blade template + JS bundle.
    // Debug-level log so a redirect loop is diagnosable without production noise.
    console.debug(`[portal] Page "${name}" not in this portal — full reload`);
    window.location.href = pendingVisitUrl || window.location.href;
    return new Promise(() => {}) as never;
  },
  setup({ el, App, props, plugin }) {
    // Scope @ja/portal-query's IndexedDB cache + mutation queue to the effective
    // authenticated user so impersonation (and logout/login) never serves the
    // previous identity's cached data. Impersonation forces a full reload, so
    // reading auth.user.id here keeps the offline cache identity in sync.
    setActiveIdentity(extractAuthIdentity(props.initialPage.props));

    createApp({ render: () => h(App, props) })
      .use(plugin)
      .use(i18n)
      .use(VueQueryPlugin, { queryClient })
      .mount(el);
  }
});
